import { api, unwrap } from "@lumberset/api-client";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import type { FormerOwner, MemberRole } from "./queries-workspaces";

/** People: everyone who can sign in, one person's page, and what an Admin changes about them. */

const fetchPeople = (q: string, page: number) =>
  unwrap(api.admin.people.$get({ query: { q: q || undefined, page: String(page) } }));

export type PeoplePage = Awaited<ReturnType<typeof fetchPeople>>;
export type PersonRow = PeoplePage["people"][number];

/** One page of the list, newest first. The last page stays while the next one loads. */
export const peopleQuery = (q: string, page: number) =>
  queryOptions({
    queryKey: ["admin", "people", { q, page }],
    queryFn: () => fetchPeople(q, page),
    placeholderData: (previous) => previous,
  });

const fetchPerson = (userId: string) =>
  unwrap(api.admin.people[":userId"].$get({ param: { userId } }));

export type PersonDetail = Awaited<ReturnType<typeof fetchPerson>>;

export const personQuery = (userId: string) =>
  queryOptions({ queryKey: ["admin", "people", userId], queryFn: () => fetchPerson(userId) });

export const peopleActions = {
  /** A new name or sign-in email. Answers with what changed. */
  edit: (userId: string, json: { name?: string; email?: string }) =>
    unwrap(api.admin.people[":userId"].$patch({ param: { userId }, json })),
  /** Puts them in a workspace with a role; as Owner they take it over. Answers with a `message`. */
  addToWorkspace: (userId: string, yardId: string, role: MemberRole, formerOwner?: FormerOwner) =>
    unwrap(
      api.admin.people[":userId"].workspaces.$post({
        param: { userId },
        json: { yardId, role, formerOwner },
      }),
    ),
  /** A link to choose a new password, in the app they sign in to. Answers with a `message`. */
  passwordReset: (userId: string) =>
    unwrap(api.admin.people[":userId"]["password-reset"].$post({ param: { userId } })),
};

/**
 * After a change to someone: their page, the People list, and the pages that
 * show them elsewhere (workspaces, the Staff list) are stale.
 */
export function useRefreshPerson() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "people"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "workspaces"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "yards"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "team"] }),
    ]);
}
